/**
 * Исходы мини-игр и «тяга» (план 2b, Task 2; interactives.md, раздел E).
 *
 * Движок ничего не знает о правилах мини-игр: пакет @life/minigames считает
 * исход `MinigameOutcome`, а здесь он применяется к `Life`. Тип объявлен
 * в движке (и реэкспортируется в @life/minigames), чтобы движок не зависел
 * от пакета мини-игр.
 *
 * Тяга — скрытый `flags.urge` 0–100: растёт от исходов (`urgeDelta`)
 * и падает на URGE_DECAY каждый год (тик делает спад до игры года, так что
 * одна игра в год держит тягу у нуля, а к порогу ведут серии выигрышей и
 * автоповтор — калибровка плана 2b, Task 11). При тяге ≥ URGE_THRESHOLD жизнь
 * получает состояние `gambling`: счастье GAMBLING_HAPPINESS в год и
 * ежегодная запись `urge.offer` («сыграть ещё»). Блок помощи
 * `help.gambling` пишется при первом переходе через порог вверх и при любом
 * проигрыше с долгом. Ниже порога состояние снимается (выход — годы без игры).
 *
 * Деньги клампятся на нуле, состояния — в [0, 100]. Вход не мутируется.
 */
import { clampStats } from './focus';
import type { Rng } from './rng';
import type { Life, LogEntry, Stats } from './types';

/** Исход мини-игры, который движок применяет к жизни (структурно тот же, что в @life/minigames). */
export interface MinigameOutcome {
  /** Доллары ППС; деньги клампятся на нуле. */
  moneyDelta?: number;
  stats?: Partial<Stats>;
  /** Сдвиг скрытой «тяги» 0–100. */
  urgeDelta?: number;
  /** Для триггера `decision`: вариант, который клиент отдаёт в `YearInput.answer`. */
  decisionOption?: string;
  /** Флаги жизни; строка — для вида займа (`loanKind`). */
  flags?: Record<string, number | boolean | string>;
  /** Привычки, которые исход включает или снимает (бар, план 2d). */
  habits?: Partial<Life['habits']>;
  logKey?: string;
  logParams?: Record<string, string | number>;
  heavy?: boolean;
}

export const FLAG_URGE = 'urge';
/** Блок помощи по тяге уже показан (первый переход через порог). */
export const FLAG_URGE_HELPED = 'urgeHelped';
export const CONDITION_GAMBLING = 'gambling';
export const URGE_THRESHOLD = 60;
export const URGE_DECAY = 4;
export const URGE_MAX = 100;
/** Счастье в год при состоянии `gambling`. */
export const GAMBLING_HAPPINESS = -3;
/** Ежегодная карточка «сыграть ещё» при тяге ≥ порога. */
export const URGE_OFFER_KEY = 'urge.offer';
/** Блок помощи при игровой зависимости. */
export const HELP_GAMBLING_KEY = 'help.gambling';
/** Был проигрыш в азартной игре в этом году (дверь к займу, E10); снимается годом без игры. */
export const FLAG_LAST_LOSS = 'lastLoss';

/**
 * Отложенная ставка на спорт (E5): флаги ставятся исходом мини-игры `bets`
 * (ставка уже снята с денег), разрешаются на следующем тике `resolveBet`.
 * Флаги жизни хранят только скаляры, поэтому ставка разложена на пять ключей.
 */
export const FLAG_BET_PENDING = 'betPending';
export const FLAG_BET_TEAM = 'betPendingTeam';
export const FLAG_BET_AMOUNT = 'betPendingBet';
/** Множитель полного возврата при победе (уже с комиссией). */
export const FLAG_BET_ODDS = 'betPendingOdds';
/** Честная вероятность победы команды. */
export const FLAG_BET_P = 'betPendingP';
export const BET_WON_KEY = 'minigame.bets.won';
export const BET_LOST_KEY = 'minigame.bets.lost';
const BET_FLAGS = [FLAG_BET_PENDING, FLAG_BET_TEAM, FLAG_BET_AMOUNT, FLAG_BET_ODDS, FLAG_BET_P];

function clampUrge(x: number): number {
  if (!(x > 0)) return 0;
  return x > URGE_MAX ? URGE_MAX : x;
}

/** Текущая тяга жизни, 0 если флага нет. */
export function urgeOf(life: Life): number {
  const raw = life.flags[FLAG_URGE];
  return typeof raw === 'number' ? clampUrge(raw) : 0;
}

function hasGambling(life: Life): boolean {
  return life.conditions.includes(CONDITION_GAMBLING);
}

/** Состояние `gambling` следует за тягой: есть при ≥ порога, иначе снимается. */
function syncGamblingCondition(life: Life): Life {
  const above = urgeOf(life) >= URGE_THRESHOLD;
  if (above === hasGambling(life)) return life;
  return {
    ...life,
    conditions: above ? [...life.conditions, CONDITION_GAMBLING] : life.conditions.filter((c) => c !== CONDITION_GAMBLING),
  };
}

function withUrge(life: Life, urge: number): Life {
  return { ...life, flags: { ...life.flags, [FLAG_URGE]: clampUrge(urge) } };
}

/** Применить исход мини-игры: деньги ≥ 0, состояния в [0, 100], тяга 0..100. Записи года — в `entries`, в журнал их кладёт тик. */
export function applyMinigame(life: Life, outcome: MinigameOutcome): { life: Life; entries: LogEntry[] } {
  const entries: LogEntry[] = [];
  const urgeBefore = urgeOf(life);
  const moneyDelta = Number.isFinite(outcome.moneyDelta) ? (outcome.moneyDelta as number) : 0;
  const money = Math.max(0, life.money + moneyDelta);
  const d = outcome.stats ?? {};
  const stats = clampStats({
    health: life.stats.health + (d.health ?? 0),
    happiness: life.stats.happiness + (d.happiness ?? 0),
    knowledge: life.stats.knowledge + (d.knowledge ?? 0),
    relations: life.stats.relations + (d.relations ?? 0),
  });
  let next: Life = { ...life, money, stats, flags: { ...life.flags, ...(outcome.flags ?? {}) } };
  if (outcome.habits) next = { ...next, habits: { ...next.habits, ...outcome.habits } };
  if (outcome.urgeDelta !== undefined || FLAG_URGE in next.flags) {
    next = withUrge(next, urgeBefore + (Number.isFinite(outcome.urgeDelta) ? (outcome.urgeDelta as number) : 0));
  }
  next = syncGamblingCondition(next);

  if (outcome.logKey) {
    const entry: LogEntry = { age: life.age, key: outcome.logKey };
    if (outcome.logParams) entry.params = outcome.logParams;
    if (outcome.heavy) entry.heavy = true;
    entries.push(entry);
  }

  // Проигрыш в азартной игре (деньги вниз, тяга вверх) открывает дверь к займу (E10).
  if (moneyDelta < 0 && (outcome.urgeDelta ?? 0) > 0) next = { ...next, flags: { ...next.flags, [FLAG_LAST_LOSS]: true } };

  const crossedUp = urgeBefore < URGE_THRESHOLD && urgeOf(next) >= URGE_THRESHOLD;
  const firstCrossing = crossedUp && next.flags[FLAG_URGE_HELPED] !== true;
  const lossWithDebt = moneyDelta < 0 && next.debt > 0;
  if (firstCrossing) next = { ...next, flags: { ...next.flags, [FLAG_URGE_HELPED]: true } };
  if (firstCrossing || lossWithDebt) entries.push({ age: life.age, key: HELP_GAMBLING_KEY, heavy: true });

  return { life: next, entries };
}

/** Спад года: тяга −URGE_DECAY (не ниже 0), флаг прошлогоднего проигрыша снимается; состояние `gambling` снимается ниже порога. */
export function decayUrge(life: Life): Life {
  let next = life;
  if (FLAG_LAST_LOSS in next.flags) {
    const flags: Life['flags'] = {};
    for (const [k, v] of Object.entries(next.flags)) if (k !== FLAG_LAST_LOSS) flags[k] = v;
    next = { ...next, flags };
  }
  if (!(FLAG_URGE in next.flags)) return syncGamblingCondition(next);
  return syncGamblingCondition(withUrge(next, urgeOf(next) - URGE_DECAY));
}

/** Ежегодный эффект состояния `gambling`: счастье −3 и карточка «сыграть ещё». */
export function applyUrgeYear(life: Life): { life: Life; entries: LogEntry[] } {
  const synced = syncGamblingCondition(life);
  if (!hasGambling(synced)) return { life: synced, entries: [] };
  const stats = clampStats({ ...synced.stats, happiness: synced.stats.happiness + GAMBLING_HAPPINESS });
  return { life: { ...synced, stats }, entries: [{ age: life.age, key: URGE_OFFER_KEY }] };
}

function numberFlag(life: Life, key: string): number {
  const raw = life.flags[key];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
}

/** Разрешить отложенную ставку: жребий по `betPendingP`, при победе деньги += ставка × коэффициент; флаги ставки снимаются. */
export function resolveBet(life: Life, rng: Rng): { life: Life; entries: LogEntry[] } {
  if (life.flags[FLAG_BET_PENDING] !== true) return { life, entries: [] };
  const bet = Math.max(0, numberFlag(life, FLAG_BET_AMOUNT));
  const odds = Math.max(0, numberFlag(life, FLAG_BET_ODDS));
  const p = Math.min(1, Math.max(0, numberFlag(life, FLAG_BET_P)));
  const won = rng.chance(p);
  const payout = won ? bet * odds : 0;
  const flags: Life['flags'] = {};
  for (const [k, v] of Object.entries(life.flags)) if (!BET_FLAGS.includes(k)) flags[k] = v;
  const next: Life = { ...life, money: life.money + payout, flags };
  const entry: LogEntry = {
    age: life.age,
    key: won ? BET_WON_KEY : BET_LOST_KEY,
    params: { amount: Math.round(won ? payout : bet) },
  };
  return { life: next, entries: [entry] };
}
